import {
  CONV_MEDIA_BASE_MAX_BYTES,
  resolveScalableDmOrConvMaxUploadBytes,
  type SubscriptionTierId,
} from '@adieuu/shared';

export type ComposerSendOptions = {
  useForwardSecrecy?: boolean;
  replyToMessageId?: string;
  e2eMediaIds?: string[];
  /** Cleartext `space_media` ids (plaintext Space channels). */
  attachmentMediaIds?: string[];
  expiresInSeconds?: number;
  /** Explicit @user mention identity ids (not @here / role expansion). */
  mentionedIdentityIds?: string[];
  /**
   * Role ids to ping (Spaces). The server validates the sender's permission to
   * mention each role and persists the accepted ids as mention markers on the
   * message; recipients are resolved from those markers when messages are read.
   */
  mentionedRoleIds?: string[];
  /**
   * When true, mention channel viewers (Spaces); requires the `allowHere`
   * setting on `MentionSource`. The server validates the sender's permission and
   * persists an @here marker on the message; recipients are resolved from the
   * marker when messages are read.
   */
  mentionHere?: boolean;
  /** Thread reply: root message id. */
  threadRootId?: string;
  /** With `threadRootId`, also post the reply to the main stream (channel or conversation). */
  alsoSendToChannel?: boolean;
  /** Caller-fixed id (media outbox) so retries de-duplicate and pending rows reconcile. */
  clientMessageId?: string;
  /**
   * Plaintext Space channels: files to upload as `space_media` inside the
   * queued send, so the message shows (with local previews) before the upload.
   */
  cleartextFiles?: File[];
};

export type ComposerSendFn = (
  plaintext: string,
  options?: ComposerSendOptions,
) => Promise<unknown>;

export interface ComposerReplyContext {
  messageId: string;
  authorName: string;
  snippet: string;
  onCancel: () => void;
  onClick?: () => void;
}

export interface MentionableUser {
  id: string;
  displayName: string;
  username?: string;
  avatarUrl?: string;
}

export interface MentionableRole {
  id: string;
  name: string;
  color?: string | null;
  mentionable: boolean;
}

export interface MentionSource {
  users: MentionableUser[];
  /** Roles offered in autocomplete (already permission-filtered by the caller). */
  roles?: MentionableRole[];
  resolveMentionDisplay: (id: string) => string;
  /** Conversations: when true, @here and @everyone are offered. */
  isGroup?: boolean;
  /** Spaces: offer @here when the sender may use it. */
  allowHere?: boolean;
  /** Conversations-only sentinel; Spaces use the Everyone role instead. */
  allowEveryone?: boolean;
  /**
   * When true (Spaces), do not resolve @here/roles into identity ids on the
   * client; emit `mentionHere` / `mentionedRoleIds` so the server can validate
   * permissions and persist the mention markers. Recipients are resolved from
   * those markers when messages are read, rather than expanded on send.
   */
  serverExpandMentions?: boolean;
}

/** Sentinel IDs for group-wide mentions stored in encrypted MentionEntity payloads. */
export const MENTION_HERE_ID = '__HERE__';
export const MENTION_EVERYONE_ID = '__EVERYONE__';

export function isGroupMentionId(id: string): boolean {
  return id === MENTION_HERE_ID || id === MENTION_EVERYONE_ID;
}

export function groupMentionDisplayText(id: string): string | null {
  if (id === MENTION_HERE_ID) return 'here';
  if (id === MENTION_EVERYONE_ID) return 'everyone';
  return null;
}

export type AttachmentUploadStatus = 'pending' | 'encrypting' | 'uploading' | 'scanning' | 'done' | 'error';

export interface PendingAttachment {
  file: File;
  previewUrl: string;
  uploadStatus: AttachmentUploadStatus;
  uploadProgress: number;
  uploadError?: string;
  /** When set, this attachment already exists on the server and should not be re-uploaded. */
  existingMediaId?: string;
}

export type TrackedMentionKind = 'user' | 'role' | 'group';

export interface TrackedMention {
  identityId: string;
  offset: number;
  length: number;
  kind?: TrackedMentionKind;
}

export interface TrackedPageTag {
  pageId: string;
  offset: number;
  length: number;
}

export interface PageTagSource {
  pages: { id: string; labelDefault: string; icon?: string; aliases?: string[] }[];
  resolvePageDisplay: (pageId: string) => string;
}

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
/**
 * Concrete video types to request when reading the clipboard, which needs literal MIME
 * strings. Not an upload allowlist — {@link isVisualMediaMimeType} governs that.
 */
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;

/**
 * Returns true for media that goes down the image/video pipeline rather than transferring as
 * an opaque file.
 *
 * Every container qualifies, not a fixed list: whether a video can actually be scanned is
 * decided later by asking the browser to decode it. Naming a subset here instead would leave
 * every unnamed container (mkv, avi, 3gp) skipping preparation entirely and transferring
 * unscanned, which is a moderation bypass anyone can reach by remuxing.
 */
export function isVisualMediaMimeType(mime: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(mime) || mime.startsWith('video/');
}

/** All file types are accepted for conversation attachments. */
export function isAcceptedConversationMediaType(_mime: string): boolean {
  return true;
}


export const MAX_ATTACHMENTS = 10;

/**
 * Baseline conversation attachment size cap (Access / non-insider).
 * Prefer {@link resolveConversationComposerMediaMaxBytes} when session grants are known.
 */
export const MAX_ATTACHMENT_BYTES = CONV_MEDIA_BASE_MAX_BYTES;

export function resolveConversationComposerMediaMaxBytes(args: {
  subscriptions: SubscriptionTierId[];
  entitlements: string[];
  isLifetime: boolean;
}): number {
  return resolveScalableDmOrConvMaxUploadBytes('conv_media', args.subscriptions, {
    entitlements: args.entitlements,
    isLifetime: args.isLifetime,
  });
}

export const PLACEHOLDER_VERB_KEYS = [
  'message',
  'hi',
] as const;

export const TTL_OPTIONS: { label: string; seconds: number }[] = [
  { label: '30s', seconds: 30 },
  { label: '1 min', seconds: 60 },
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
  { label: '15 min', seconds: 900 },
  { label: '30 min', seconds: 1800 },
  { label: '45 min', seconds: 2700 },
  { label: '1 hr', seconds: 3600 },
  { label: '1.5 hr', seconds: 5400 },
  { label: '3 hr', seconds: 10800 },
  { label: '6 hr', seconds: 21600 },
  { label: '12 hr', seconds: 43200 },
  { label: '18 hr', seconds: 64800 },
  { label: '24 hr', seconds: 86400 },
  { label: '36 hr', seconds: 129600 },
  { label: '48 hr', seconds: 172800 },
  { label: '1 week', seconds: 604800 },
  { label: '2 weeks', seconds: 1209600 },
];

export type ComposerControlId =
  | 'forwardSecrecy'
  | 'timedMessage'
  | 'upload'
  | 'gif'
  | 'emoji'
  | 'send';

export type ComposerControlSide = 'left' | 'right';

export type ComposerSendIconId =
  | 'paper-plane'
  | 'mailbox'
  | 'arrow-right'
  | 'message-arrow-up'
  | 'message-arrow-up-right';

export interface ComposerControlConfig {
  id: ComposerControlId;
  enabled: boolean;
  side: ComposerControlSide;
  order: number;
  sendIcon?: ComposerSendIconId;
  /** When true, the send control shows a "Send" label beside the icon. */
  sendShowText?: boolean;
}
